import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { AsyncSubject, Subject, take } from 'rxjs';
import { Editor, RawEditorOptions, Ui } from 'tinymce';
import { FileUploader, FileUploadModule } from 'ng2-file-upload';
declare const tinymce: any;
import { Routes } from '../../../config/routes';
import { LegFiJwtService } from '../../../services/auth/legfi-jwt.service';
import { AIConstants } from '../../../config/ai-constants';
import { GrowlerService } from '../growler/growler.service';
import environment from '../../../../environments/environment';
import { FileUploaderService } from 'app/services/file-uploader.service';
import { AIService } from 'app/services/ai.service';
import { EditorComponent, EditorModule } from '@tinymce/tinymce-angular';
import { CommonModule } from '@angular/common';
import { SharedModule } from '../shared.module';

/** TODO: revisit with theming after ng update */
/** Specify content styles for tinyMCE editor loaded within iframe */
export const rteContentStyles: string = `
@font-face {
    font-family: 'ProximaNovaRegular';
    src: url('/assets/fonts/Proxima Nova Regular.otf');
    font-style: normal;
    font-weight: normal;
}

body {
    font-family: 'ProximaNovaRegular', Arial, Helvetica, sans-serif;
    font-size: 15px;
    line-height: 1.5;
}

.mce-content-body img,
.mce-content-body [contentEditable=false] {
    background: #ff0;
    cursor: default !important;
    padding: 4px 2px;
    outline-offset: -2px;
    margin: 0 1px;
}

.mce-content-body img[data-mce-selected],
.mce-content-body [contentEditable=false][data-mce-selected] {
    outline: 3px solid #ffea00;
}

.mce-content-body img {
    min-width: 50px;
    padding: 8px;
}
`;

@Component({
    selector: 'app-rich-text-editor',
    styleUrls: ['./rich-text-editor.component.scss'],
    templateUrl: './rich-text-editor.component.html',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        EditorModule,
        SharedModule,
        FileUploadModule,
        MatIconModule
    ],
})
export class RichTextEditorComponent implements OnInit
{
    @Input() label: string;
    @Input() isLoading = true;
    @Input() useNewStyles = false;

    // most have a form group
    @Input() formGroup: UntypedFormGroup;
    @Input() controlName = 'editor';

    // otherwise, use a form control (ex. formly component)
    @Input() formControl: UntypedFormControl;

    @Input() type: 'full' | 'email' | 'formly' | 'compact' = 'full';
    @Input() plugins: string[];
    @Input() toolbar: string[];
    @Input() editorHeight: number;
    @Input() placeholder: string;
    @Input() additionalInsertFields: string[] = [];
    /**
     * Org custom field tokens (e.g. 'Member Custom Field: Kids Names') shown as a
     * separate "Custom Fields" group at the bottom of the Insert Field menu.
     * Read at menu-open time, so async-loaded fields still appear.
     */
    @Input() customFieldInsertFields: string[] = [];

    @Input() isPastDueEmail: boolean = false;
    @Input() useSkinnyAI: boolean = false;
    @Input() showAttachDocument = false;
    /** When true, the image toolbar button is not added for email/compact editors. */
    @Input() hideImageButton = false;

    @Output() editorBlur = new EventEmitter<void>();
    @Output() attachDocumentClicked = new EventEmitter<void>();

    @ViewChild('attachFile') attachFileRef!: ElementRef;
    @ViewChild(EditorComponent) editorRef!: EditorComponent;

    // AI Instructions text from constants
    aiInstructionsText = AIConstants.TINYMCE_AI_INSTRUCTIONS_TEXT;
    showAIInstructions = false; // Only show if AI plugin loads successfully

    config: RawEditorOptions;

    _uploader: FileUploader;
    _callback: (value: string, meta?: Record<string, any>) => void;

    readonly pluginOptions = [
        'lists',
        'link autolink',
        'image',
        'table',
        'code',
        'wordcount',
        'ai',
        // 'emoticons', holding off until tested with Gotenberg PDF exports
    ];
    readonly toolbarOptions = [
        'undo redo',
        'styles',
        'bold italic underline link emoticons',
        'aidialog aishortcuts',
        'numlist bullist',
        'alignleft aligncenter alignright alignjustify',
        'outdent indent',
    ];
    private readonly editorOptions: RawEditorOptions = {
        base_url: '/tinymce', // Use local TinyMCE assets instead of CDN
        suffix: '.min', // Use minified files
        license_key: 'gpl', // Use GPL license for self-hosted

        // Premium plugins configuration - required for AI plugin
        // Note: external_plugins will be set in ngOnInit with the environment API key
        external_plugins: null,

        branding: false,
        contextmenu: false,
        menubar: false,
        selector: 'app-rich-text-editor',
        plugins: null,
        toolbar: null,
        link_assume_external_targets: 'http',
        // SD-7958: TinyMCE defaults convert_urls + relative_urls to true, which rewrite any
        // same-host absolute URL (e.g. https://app.payhoa.com/app/surveys/9219) into a relative
        // path on save, dropping the scheme+domain. The stored relative href has no base in
        // outbound emails, so the link fails to load. Disable URL conversion so absolute URLs
        // are persisted verbatim.
        convert_urls: false,
        elementpath: false,
        noneditable_class: 'placeholder',
        content_style: rteContentStyles,
        browser_spellcheck: true,
        ui_mode: 'split', // Place popup sink adjacent to editor, not at body level
        draggable_modal: true, // Enable dragging for modal dialogs
        ai_request: null, // Will be set in ngOnInit
        ai_shortcuts: AIConstants.TINYMCE_AI_SHORTCUTS,
    };
    private editorSubject: Subject<any> = new AsyncSubject();

    // Helper method to clean AI responses
    private cleanAIResponse(response: string): string {
        // Remove markdown code blocks and language tags
        return response
            .replace(/```[\w]*\n?/gi, '')  // Remove ```html, ```javascript, etc.
            .replace(/```\n?/g, '')        // Remove closing ```
            .replace(/^html\n?/mi, '')     // Remove standalone "html" at start
            .replace(/^\w+\n/mi, '')       // Remove any language identifier on first line
            .trim();
    }

    // AI Provider Methods - Using Secure Backend Proxy via AIService
    makeAIRequest = (request: any): Promise<string> => {
        return this._aiService.makeRequest(request);
    };



    constructor(
            private _fb: UntypedFormBuilder,
            // TODO: replace with JWT removal and session state updates
            private _legFiJwtService: LegFiJwtService,
            private _growler: GrowlerService,
            private _fileUploaderService: FileUploaderService,
            private _http: HttpClient,
            private _aiService: AIService,
    ) {
    }

    get control() {
        return this.formControl ? this.formControl : this.formGroup.get(this.controlName) as UntypedFormControl;
    }

    get height() {
        // Use custom height if provided, otherwise use defaults based on type
        if (this.editorHeight) {
            return this.editorHeight;
        }

        switch (this.type) {
            case 'compact':
                return 280;
            case 'formly':
                return 300;
            default:
                return 500;
        }
    }

    ngOnInit() {
        if (!this.formGroup && !this.formControl) {
            this.formGroup = this._fb.group({
                [this.controlName]: [''],
            });
        }

        if (!this.plugins) {
            this.plugins = this.pluginOptions;
        }

        const customToolbarProvided = !!this.toolbar; // Track if custom toolbar was provided
        if (!this.toolbar) {
            this.toolbar = this.toolbarOptions;
        }

        const config: RawEditorOptions = {
            ...this.editorOptions,
            height: this.height,
            placeholder: this.placeholder || this.editorOptions.placeholder,
            // Configure premium AI plugin to load from TinyMCE CDN with API key
            external_plugins: {
                'ai': `https://cdn.tiny.cloud/1/${environment.TinyMCE.appId}/tinymce/7-stable/plugins/ai/plugin.min.js`
            },
            ai_request: (request, respondWith) => {
                // Using secure backend proxy to protect API keys

                return respondWith.string(() => {
                    // Handle selection logic: respect user selection, track full content editing state
                    const editorInstance = tinymce?.activeEditor;
                    let isEditingFullContent = false;

                    if (editorInstance) {
                        const selectedText = editorInstance.selection.getContent();
                        const currentContent = editorInstance.getContent();

                        // If user has selected text, use that as context
                        if (selectedText && selectedText.trim()) {
                            request.context = selectedText;
                            isEditingFullContent = false;
                        }
                        // If no text is selected but there is content, use all content as context
                        else if (currentContent && currentContent.trim()) {
                            request.context = currentContent;
                            isEditingFullContent = true;
                            // Store the flag for later use in the response handling
                            (request as any)._isEditingFullContent = true;
                        }
                    }

                    return this.makeAIRequest(request);
                });
            }
        };

        // TODO: replace with JWT removal and session state updates
        const jwt = this._legFiJwtService.readOrRedirect();
        if (jwt.superUser) {
            // adding for supers for now
            this.toolbar.push('code');
            config.elementpath = true;
        }

        // Promote AI up front for email/compact editors — it's a flagship feature and
        // shouldn't be buried behind the overflow menu. Pull 'aidialog aishortcuts' out
        // of its default position and re-insert it right after 'Insert Field'.
        // Order: undo/redo → Insert Field → AI → [Attach] → [image] → styles → bold/italic/… → rest
        if ((this.type === 'email' || this.type === 'compact') && !customToolbarProvided) {
            const aiIdx = this.toolbar.indexOf('aidialog aishortcuts');
            if (aiIdx !== -1) { this.toolbar.splice(aiIdx, 1); }

            this.toolbar.splice(1, 0, 'stringToken');
            this.toolbar.splice(2, 0, 'aidialog aishortcuts');
            let nextIdx = 3;
            if (this.showAttachDocument) {
                this.toolbar.splice(nextIdx, 0, 'attachDocument');
                nextIdx++;
            }
            if (!this.hideImageButton) {
                this.toolbar.splice(nextIdx, 0, 'image');
            }
            config.image_caption = true;
            config.automatic_uploads = true;
            config.file_picker_types = 'image';
            config.file_picker_callback = (callback) => {
                this._callback = callback;
                this.attachFileRef.nativeElement.click();
            };
            // SD-2822: Handle pasted/dragged images by uploading them to the server
            // instead of embedding as base64 (which can exceed SQS message size limits)
            config.images_upload_handler = (blobInfo: any) => {
                return new Promise<string>((resolve, reject) => {
                    const jwt = LegFiJwtService.read();
                    if (!jwt) {
                        reject('Not authenticated');
                        return;
                    }

                    // Generate filename - blobInfo.filename() may be undefined for pasted images
                    const filename = blobInfo.filename() || `pasted-image-${Date.now()}.png`;
                    // SD-7843: upload as a plain UploadedFile (/file/upload) instead of the
                    // email-attach documents endpoint — inline editor images must never be
                    // saved as Documents / appear on the Documents page.
                    const uploadUrl = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UploadFile());

                    const formData = new FormData();
                    formData.append('file', blobInfo.blob(), filename);
                    formData.append('context', 'communication');

                    this._http.post(uploadUrl, formData).pipe(take(1)).subscribe({
                        next: (response: any) => {
                            if (response?.viewUrl) {
                                resolve(response.viewUrl);
                            } else {
                                reject('Upload failed: No URL returned');
                            }
                        },
                        error: (error) => {
                            const message = error?.error?.message || error?.message || 'Image upload failed';
                            this._growler.error('Image Upload Failed', message);
                            reject(message);
                        }
                    });
                });
            };
            config.init_instance_callback = function (e) {
                    if (config.elementpath) {
                        const container = e.editorContainer;
                        const elementpath = container.querySelector('.tox-statusbar__path');
                        const tooltip = `Path for the current selection's parent HTML elements (e.g. p > em > strong > span)`;
                        elementpath.setAttribute('title', tooltip);
                    }

                    // Hook into AI content insertion to handle full content replacement
                    e.on('beforeSetContent', function(event) {
                        // Check if this is an AI insertion and we need full replacement
                        if ((window as any)._tinyMCE_isEditingFullContent &&
                            event.content &&
                            !event.initial) {

                            // For full content replacement, clear all content first
                            e.setContent('');
                            // Reset the flag after handling
                            (window as any)._tinyMCE_isEditingFullContent = false;
                        }
                    });

                    // Hook into dialog opening to apply custom sizing immediately
                    e.on('OpenWindow', function(event) {
                        setTimeout(() => {
                            const dialog = document.querySelector('.tox-dialog');

                            // Check if this is an AI dialog by various indicators
                            const isAIDialog = dialog && (
                                dialog.getAttribute('aria-label')?.includes('AI') ||
                                dialog.querySelector('[role="dialog"]')?.getAttribute('aria-label')?.includes('AI') ||
                                dialog.textContent?.includes('AI Assistant') ||
                                dialog.querySelector('.tox-dialog__title')?.textContent?.includes('AI')
                            );

                            if (isAIDialog) {
                                const dialogEl = dialog as HTMLElement;
                                const screenWidth = window.innerWidth;
                                const isMobileView = screenWidth <= 768;
                                const shouldUseSkinnyAI = this.useSkinnyAI || isMobileView;

                                if (shouldUseSkinnyAI) {
                                    // Skinny AI styling
                                    dialogEl.style.setProperty('width', '300px', 'important');
                                    dialogEl.style.setProperty('max-width', 'min(300px, 95vw)', 'important');
                                    dialogEl.style.setProperty('min-width', '280px', 'important');
                                    dialogEl.style.setProperty('min-height', '250px', 'important');
                                    dialogEl.style.setProperty('max-height', '80vh', 'important');
                                } else {
                                    // Standard AI styling for larger screens
                                    dialogEl.style.setProperty('width', '500px', 'important');
                                    dialogEl.style.setProperty('max-width', 'min(500px, 85vw)', 'important');
                                    dialogEl.style.setProperty('min-width', '420px', 'important');
                                    dialogEl.style.setProperty('min-height', '350px', 'important');
                                    dialogEl.style.setProperty('max-height', '75vh', 'important');
                                }

                                // Always center in viewport regardless of editor position
                                dialogEl.style.setProperty('position', 'fixed', 'important');
                                dialogEl.style.setProperty('top', '50%', 'important');
                                dialogEl.style.setProperty('left', '50%', 'important');
                                dialogEl.style.setProperty('transform', 'translate(-50%, -50%)', 'important');
                                dialogEl.style.setProperty('z-index', '9999', 'important');
                                dialogEl.style.setProperty('margin', '0', 'important');
                            } else {
                                // No AI dialog found
                            }
                        }, 10); // Small delay to ensure DOM is ready
                    });
            };

            // rendering the default message text but having it change if it's a Past Due Email.
            if (this.isPastDueEmail) {
                this.control.setValue(
                        `${this.getStringTokenContent('current date')} <br><br>` +
                        `${this.getStringTokenContent('first name')} ${this.getStringTokenContent('last name')} <br><br>` +
                        `This is a friendly reminder that your account with ${this.getStringTokenContent('community name')} is now past due, ` +
                        `in the amount of ${this.getStringTokenContent('past-due balance')}. Please make a payment at your earliest convenience. ` +
                        `To ${this.getStringTokenContent('payment link')}, log on to your PayHOA account  to pay with a credit card or e-check.<br> <br>` +
                        `If you have any questions, please reply to this email.<br><br>` +
                        `${this.getStringTokenContent('community name')}<br>` +
                        `Thank you.`
                );
            } else  if (this.type === 'email') {
                this.control.setValue(`Hi ${this.getStringTokenContent('first name')}, `);
            }
        }

        this.config = {
            ...config,
            plugins: this.plugins.join(' '),
            toolbar: this.toolbar.join(' | '),
        };
    }

    handleEditorInit(e: any) {
        this.editorSubject.next(e.editor);
        this.editorSubject.complete();

        if (this.type === 'email' || this.type === 'compact') {
            this.resetStringToken(e.editor);
            this.resetFileUploader();
        }

        if (this.showAttachDocument) {
            this.registerAttachDocumentButton(e.editor);
        }

        // Only show AI instructions if the plugin loaded successfully
        const aiPluginLoaded = e.editor?.plugins?.ai !== undefined;
        if (aiPluginLoaded) {
            this.showAIInstructions = true;
        }

        this.isLoading = false;
    }

    focus() {
        this.editorSubject.pipe(take(1)).subscribe(editor => editor?.focus());
    }

    onEditorBlur(): void {
        this.editorBlur.emit();
    }

    getStringTokenContent(text: string) {
        return `<span class="${this.editorOptions.noneditable_class}">{${text}}</span>`;
    }

    /** helper/init method to set "insert field" placeholders in editor */
    private resetStringToken(editor: Editor) {
        const placeholders = [
            'balance',
            'past-due balance',
            'unit title',
            'unit title + balance',
            'unit title + past-due balance',
            'unit address',
            'unit address + balance',
            'unit address + past-due balance',
            'first name',
            'last name',
            'mailing address',
            'community name',
            'payment link',
            'current date',
            'login qr code',
        ];

        const allPlaceholders = [...placeholders, ...this.additionalInsertFields];

        editor.ui.registry.addMenuButton('stringToken', {
            text: 'Insert Field',
            tooltip: 'Insert field to populate with data from PayHOA',
            fetch: (callback) => {
                const items: Ui.Menu.NestedMenuItemContents[] = [];
                for (const placeholder of allPlaceholders) {
                    items.push({
                        type: 'menuitem',
                        text: placeholder,
                        onAction: () => editor.insertContent(this.getStringTokenContent(placeholder)),
                    });
                }
                // org custom fields render as their own group below the built-in
                // fields; omitted entirely when the org has none
                if (this.customFieldInsertFields.length) {
                    items.push({ type: 'separator', text: 'Custom Fields' });
                    for (const placeholder of this.customFieldInsertFields) {
                        items.push({
                            type: 'menuitem',
                            text: placeholder,
                            onAction: () => editor.insertContent(this.getStringTokenContent(placeholder)),
                        });
                    }
                }
                callback(items);
            },
        });
    }

    /** Register a custom toolbar button for attaching documents */
    private registerAttachDocumentButton(editor: any): void {
        editor.ui.registry.addButton('attachDocument', {
            icon: 'browse',
            tooltip: 'Attach File',
            onAction: () => this.attachDocumentClicked.emit(),
        });
    }

    /** helper/init method to get file uploader ready or reset */
    private resetFileUploader() {
        // TODO: replace with JWT removal and session state updates
        const jwt = this._legFiJwtService.readOrRedirect();
        if (jwt !== null) {
            // SD-7843: upload as a plain UploadedFile (/file/upload) instead of the
            // email-attach documents endpoint — inline editor images must never be
            // saved as Documents / appear on the Documents page.
            this._uploader = this._fileUploaderService.initUploader({
                url: Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UploadFile()),
                autoUpload: true,
                // Validate against the communications whitelist, matching the email
                // uploader (SD-7783).
                additionalParameter: { context: 'communication' },
            });

            this._uploader.onCompleteItem = (
                    fileItem: any,
                    response: string,
                    status: number,
            ) => {
                if (status < 200 || status > 206) {
                    // Surface the API's specific reason (e.g. unsupported type / too
                    // large) instead of a generic message, matching email-sender-dialog.
                    let message = 'There was a problem uploading your file.';
                    try {
                        message = JSON.parse(response)?.message || message;
                    } catch {
                        // response was not JSON — keep the generic message
                    }
                    this._growler.error('Error', message);
                    this.resetFileUploader();
                    return;
                }

                const result = JSON.parse(response);
                this._callback(result.viewUrl, {title: fileItem.file.name, alt: fileItem.name});
                this.resetFileUploader();
            };
        }
    }
}
